import "./App.css";
import { useFrame } from "@react-three/fiber";
import { OrbitControls, useTexture } from "@react-three/drei";
import { useRef, useMemo, useEffect, useCallback } from "react";
import sound1 from "./sounds/sound1.mp3";
import sound2 from "./sounds/sound2.mp3";
import sound3 from "./sounds/sound3.mp3";
import sound4 from "./sounds/sound4.mp3";
import sound5 from "./sounds/sound5.mp3";

export default function Spheres({ audioContext, destination }) {
  const textures = useTexture([
    "./normal1.jpg",
    "./normal2.jpg",
    "./normal3.jpg",
    "./normal4.jpg",
    "./normal5.jpg",
    "./normal6.jpg",
    "./normal7.jpg",
    "./normal8.jpg",
  ]);

  const colors = [
    "#ffb3b3",
    "#F7E6B2",
    "#E0A900",
    "#BA6368",
    "#FFCD69",
    "#C7FC62",
    "#E5FFB3",
    "#BADFFF",
  ];

  const audioElementsRef = useRef([]);
  const gainNodesRef = useRef([]);

  // Create HTML5 Audio elements and connect them to Web Audio API
  useEffect(() => {
    if (!audioContext || !destination) return;

    const sounds = [sound1, sound2, sound3, sound4, sound5];
    const volumes = [0.1, 0.1, 0.05, 0.09, 0.2];

    sounds.forEach((soundUrl, index) => {
      if (audioElementsRef.current[index]) return;
      
      const audio = new Audio(soundUrl);
      audio.preload = 'auto';
      audio.load();
      
      const source = audioContext.createMediaElementSource(audio);
      const gainNode = audioContext.createGain();
      gainNode.gain.value = volumes[index];
      
      source.connect(gainNode);
      gainNode.connect(destination);
      gainNode.connect(audioContext.destination);
      
      audioElementsRef.current[index] = audio;
      gainNodesRef.current[index] = gainNode;
    });

    return () => {
      audioElementsRef.current.forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      audioElementsRef.current = [];
      gainNodesRef.current = [];
    };
  }, [audioContext, destination]);

  const playSound = useCallback((index) => {
    const audio = audioElementsRef.current[index];
    if (!audio || !audioContext) return;

    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    audio.currentTime = 0;
    audio.play().catch(err => console.error('Playback error:', err));
  }, [audioContext]);

  const randomItem = (items) => items[Math.floor(Math.random() * items.length)];

  const sphereData = useMemo(() => {
    if (!textures || textures.length === 0) return [];
    
    return [
      {
        position: [3, 1.6, 1],
        scale: 0.7,
        color: "#FFBFC3",
        soundIndex: 4,
        rotation: [0, 0.6, 0.6],
        texture: randomItem(textures),
      },
      {
        position: [-2, 2, -2],
        scale: 0.7,
        color: "#E6D62E",
        soundIndex: 3,
        rotation: [0, 0, 0.6],
        texture: randomItem(textures),
      },
      {
        position: [2.2, 1, -5],
        scale: 1.2,
        color: "#FFBFC3",
        soundIndex: 2,
        rotation: [0, 0, 0.5],
        texture: randomItem(textures),
      },
      {
        position: [6, -2, -2.5],
        scale: 0.9,
        color: "#BADFFF",
        soundIndex: 1,
        rotation: [0, 0.6, 0],
        texture: randomItem(textures),
      },
      {
        position: [1.1, -3.2, -0.9],
        scale: 1,
        color: "#EBDCAB",
        soundIndex: 1,
        rotation: [0.6, 0, 0],
        texture: randomItem(textures),
      },
      {
        position: [-2.5, -1.2, -2],
        scale: 0.8,
        color: "#F0BB0E",
        soundIndex: 0,
        rotation: [0.2, 0, 0],
        texture: randomItem(textures),
      },
      {
        position: [-5, 2, -6],
        scale: 1.2,
        color: "#FFBFC3",
        soundIndex: 3,
        rotation: [0, 0, 0.6],
        texture: randomItem(textures),
      },
      {
        position: [-6, -6, -1.8],
        scale: 1.5,
        color: "#F77B23",
        soundIndex: 2,
        rotation: [0, 0, 0.6],
        texture: randomItem(textures),
      },
      {
        position: [1, -5, 3],
        scale: 0.9,
        color: "#F0BB0E",
        soundIndex: 4,
        rotation: [0.6, 0.3, 0],
        texture: randomItem(textures),
      },
      {
        position: [-7, 1.2, -1.2],
        scale: 1.3,
        color: "#F77B23",
        soundIndex: 1,
        rotation: [0, 0, 0.6],
        texture: randomItem(textures),
      },
      {
        position: [1, -2, 6],
        scale: 1.2,
        color: "#E6D62E",
        soundIndex: 0,
        rotation: [0.6, 0, 0],
        texture: randomItem(textures),
      },
      {
        position: [1, 2, -10],
        scale: 0.9,
        color: "#EBDCAB",
        soundIndex: 4,
        rotation: [0, 0, 0.9],
        texture: randomItem(textures),
      },
    ];
  }, [textures]);

  const sphereRefs = useRef([]);

  useFrame((state, delta) => {
    sphereRefs.current.forEach((ref, i) => {
      if (!ref) return;
      const [rx, ry, rz] = sphereData[i].rotation;
      ref.rotation.x += delta * rx;
      ref.rotation.y += delta * ry;
      ref.rotation.z += delta * rz;
    });
  });

  const handleSphereClick = (evt, soundIndex) => {
    evt.object.material.color.set(randomItem(colors));
    playSound(soundIndex);
  };

  const pointerEvents = {
    onPointerEnter: () => (document.body.style.cursor = "pointer"),
    onPointerLeave: () => (document.body.style.cursor = "auto"),
  };

  return (
    <>
      <OrbitControls
        minDistance={2}
        maxDistance={10}
        target={[-0.7, -0.8, -2.2]}
      />
      <directionalLight position={[1, 1, 3]} intensity={1} color="white" />
      <ambientLight intensity={1.2} color="white" />
      {sphereData.map((s, i) => (
        <mesh
          key={i}
          ref={(el) => (sphereRefs.current[i] = el)}
          position={s.position}
          scale={s.scale}
          onClick={(evt) => handleSphereClick(evt, s.soundIndex)}
          onContextMenu={(evt) => handleSphereClick(evt, s.soundIndex)}
          {...pointerEvents}
        >
          <sphereGeometry />
          <meshStandardMaterial color={s.color} map={s.texture} />
        </mesh>
      ))}
    </>
  );
}